import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { StandardTable } from '../../../../shared/components/standard-table/standard-table/standard-table';
import { FormsModule } from '@angular/forms';
import { AccountService } from '../../../../core/services/account.service';
import { CardService } from '../../../../core/services/card.service';
import { AlertService } from '../../../../shared/components/alert/alert';
import { TranslationService } from '../../../../core/services/translation.service';
import { UploadImageService } from '../../../../core/services/uploadimage.service';
import { firstValueFrom } from 'rxjs';
import { TranslatePipe } from '../../../../shared/pipes/translate.pipe';

@Component({
  selector: 'app-account',
  imports: [CommonModule, StandardTable, FormsModule, TranslatePipe],
  templateUrl: './account.html',
  styleUrl: './account.css',
})
export class Account implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  columnList = [
    { name: 'column.name', key: 'name', sortable: false },
    { name: 'column.phone', key: 'phone', sortable: false },
    { name: 'column.card', key: 'cardname', sortable: false },
    { name: 'column.QR', key: 'image', sortable: false },
    { name: 'column.action', key: 'actions', sortable: false },
  ];

  tableDataFilters: any[] = [];
  totalItems = 0;
  isLoading = false;

  currentPage = 1;
  pageSize = 10;
  searchQuery = '';

  cards: any = [];

  isSaving = false;
  isModalOpen = false;
  isEditMode = false;
  editingId: string | null = null;
  isDeleteConfirmOpen = false;
  deletingRow: any = null;

  newAccount: any = {
    cardid: '',
    name: '',
    phone: '',
    t1: '',
  };

  imagePreview: string | null = null;
  editImage: string | null = null;
  imageFile: File | null = null;

  constructor(
    private accountService: AccountService,
    private cardService: CardService,
    private uploadImageService: UploadImageService,
    private cdr: ChangeDetectorRef,
    private alertService: AlertService,
    private translationService: TranslationService,
  ) {}

  imageBaseUrl = 'https://ts-space.sgp1.digitaloceanspaces.com/uploads/lottery/admin/image/';
  cardBaseUrl = 'https://ts-space.sgp1.digitaloceanspaces.com/uploads/lottery/admin/cards/';

  ngOnInit(): void {
    this.loadAccounts();
    this.loadCards();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.imagePreview = e.target.result;
        this.imageFile = file;
        this.cdr.detectChanges();
      };
      reader.readAsDataURL(file);
    }
  }

  loadAccounts() {
    this.isLoading = true;

    const payload = {

      page: this.currentPage.toString(),
      limit: this.pageSize.toString(),
      search: this.searchQuery,
    };

    this.accountService.getAllAccounts(payload).subscribe({
      next: (res: any) => {
        if (res.returncode === '300') {
          this.tableDataFilters = res.data.data.map((t: any) => ({
            ...t,
            cardname: t.card?.name || '',
            image: t.t1,
            name: t.name || '—',
            phone: t.phone || '—',
          }));
          this.totalItems = res.data.meta.totalItems || res.data.meta.total || 0;
        } else {
          this.tableDataFilters = [];
          this.totalItems = 0;
        }
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to fetch accounts in admin table:', err);
        this.tableDataFilters = [];
        this.totalItems = 0;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  loadCards() {
    const payload = {
      page: 1,
      limit: 50,
    };

    this.cardService.getAllCards(payload).subscribe({
      next: (res: any) => {
        if (res.returncode === '300') {
          this.cards = res.data.data;
        } else {
          this.cards = [];
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to fetch cards in admin table:', err);
        this.cards = [];
        this.cdr.detectChanges();
      },
    });
  }

  search(value: string) {
    this.searchQuery = value;
    this.currentPage = 1;
    this.loadAccounts();
  }

  onSort(column: any) {
    console.log('Sort:', column);
  }

  onPageChange(page: any) {
    this.currentPage = page;
    this.loadAccounts();
  }

  onClickRow(row: any) {
    console.log('Row clicked:', row);
  }

  openCreateModal() {
    this.isEditMode = false;
    this.editingId = null;
    this.isModalOpen = true;
    this.imagePreview = null;
    this.imageFile = null;
    this.newAccount = {
      cardid: this.cards[0].id,
      name: '',
      phone: '',
      t1: '',
    };
  }

  onEdit(row: any) {
    this.isEditMode = true;
    this.editingId = row.id;
    this.isModalOpen = true;
    
    const imageUrl = row.t1 || row.image;
    this.imagePreview = imageUrl ? (imageUrl.startsWith('http') ? imageUrl : this.imageBaseUrl + imageUrl) : null;
    this.editImage = this.imagePreview;
    
    this.imageFile = null;
    this.newAccount = {
      cardid: row.cardid,
      name: row.name === '—' ? '' : (row.name || ''),
      phone: row.phone === '—' ? '' : (row.phone || ''),
      t1: row.t1,
    };
  }

  onDelete(row: any) {
    this.deletingRow = row;
    this.isDeleteConfirmOpen = true;
  }

  confirmDelete() {
    const row = this.deletingRow;
    if (!row) return;
    this.isDeleteConfirmOpen = false;
    this.deletingRow = null;

    this.accountService.deleteAccount(row.id).subscribe({
      next: (res: any) => {
        if (res.returncode === '300') {
          this.alertService.success(this.translationService.translate('alert.deleteSuccess'));
          if (row.t1) {
            this.uploadImageService.deleteImage(row.t1).subscribe({
              next: () => console.log('Account image deleted successfully'),
              error: (e) => console.error('Failed to delete account image:', e),
            });
          }
          this.loadAccounts();
        } else {
          this.alertService.error(res.message || this.translationService.translate('alert.deleteError'));
        }
      },
      error: (err) => {
        console.error('Error deleting account:', err);
        this.alertService.error(this.translationService.translate('alert.errorOccurred'));
      },
    });
  }

  cancelDelete() {
    this.isDeleteConfirmOpen = false;
    this.deletingRow = null;
  }

  closeModal() {
    this.isModalOpen = false;
  }

  async submit() {
    if (!this.newAccount.cardid) return;

    this.isSaving = true;

    try {
      if (this.imagePreview && this.imagePreview !== this.editImage && this.imageFile) {
        const res = await firstValueFrom(
          this.uploadImageService.uploadImageForAdmin('account', this.imageFile)
        );

        if (res.returncode !== '300') {
          throw new Error(res.message);
        }

        this.newAccount.t1 = res.data.url;
        this.imagePreview = res.data.url;
        this.editImage = res.data.url;
      }

      const request$ = this.isEditMode
        ? this.accountService.updateAccount({ ...this.newAccount, id: this.editingId })
        : this.accountService.createAccount(this.newAccount);

      request$.subscribe({
        next: (res: any) => {
          this.isSaving = false;
          if (res.returncode === '300') {
            this.alertService.success(
              this.translationService.translate(this.isEditMode ? 'alert.updateSuccess' : 'alert.createSuccess'),
            );
            this.closeModal();
            this.loadAccounts();
          } else {
            this.alertService.error(
              res.message || this.translationService.translate(this.isEditMode ? 'alert.updateError' : 'alert.createError'),
            );
            
            if (this.newAccount.t1 && this.newAccount.t1 !== this.editImage) {
              this.uploadImageService.deleteImage(this.newAccount.t1).subscribe({
                next: () => console.log('Orphaned image deleted'),
                error: (e) => console.error('Failed to delete orphaned image', e)
              });
            }
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.isSaving = false;
          console.error(`Error ${this.isEditMode ? 'updating' : 'creating'} account:`, err);
          this.alertService.error(this.translationService.translate('alert.errorOccurred'));
          
          if (this.newAccount.t1 && this.newAccount.t1 !== this.editImage) {
            this.uploadImageService.deleteImage(this.newAccount.t1).subscribe({
              next: () => console.log('Orphaned image deleted on error'),
              error: (e) => console.error('Failed to delete orphaned image on error', e)
            });
          }
          this.cdr.detectChanges();
        },
      });
    } catch (error: any) {
      this.isSaving = false;
      this.alertService.error(
        error.message || this.translationService.translate(this.isEditMode ? 'alert.updateError' : 'alert.createError')
      );
      this.cdr.detectChanges();
    }
  }
}
